/**
 * CSV generation.
 *
 * Four system layouts cover the common payroll hand-offs; a custom profile
 * lets an admin reorder, rename and drop columns to match a specific bureau's
 * import template without needing a code change.
 */

import { aggregateRows } from './aggregate';
import type {
  ExportProfileColumn,
  ExportProfileKind,
  PayPeriod,
  PayrollExportProfile,
  PayrollExportResult,
  Timesheet,
} from './types';

export const PAYROLL_EXPORT_KINDS: { kind: ExportProfileKind; label: string; description: string }[] = [
  { kind: 'shift_detail', label: 'Shift detail', description: 'One row per shift with hours, premiums and pay.' },
  { kind: 'employee_summary', label: 'Employee summary', description: 'One row per employee for the period.' },
  { kind: 'timesheet_register', label: 'Timesheet register', description: 'Approval status and totals per timesheet.' },
  { kind: 'cost_centre', label: 'Cost centre breakdown', description: 'Hours and pay grouped by cost centre and site.' },
];

type CellFormat = NonNullable<ExportProfileColumn['format']>;

export const SHIFT_DETAIL_COLUMNS: ExportProfileColumn[] = [
  { source: 'shiftExecutionId', header: 'Shift Execution ID', format: 'text' },
  { source: 'employeeId', header: 'Employee ID', format: 'text' },
  { source: 'payrollReference', header: 'Payroll reference', format: 'text' },
  { source: 'employeeName', header: 'Employee Name', format: 'text' },
  { source: 'payBandCode', header: 'Pay band', format: 'text' },
  { source: 'payBandName', header: 'Pay band name', format: 'text' },
  { source: 'employmentType', header: 'Employment type', format: 'text' },
  { source: 'costCentre', header: 'Cost centre', format: 'text' },
  { source: 'rosterDate', header: 'Roster date', format: 'date' },
  { source: 'weekStartDate', header: 'Week starting', format: 'date' },
  { source: 'siteName', header: 'Site', format: 'text' },
  { source: 'localPlannedStart', header: 'Planned start', format: 'text' },
  { source: 'localPlannedEnd', header: 'Planned end', format: 'text' },
  { source: 'localClockIn', header: 'Clock in local', format: 'text' },
  { source: 'localClockOut', header: 'Clock out local', format: 'text' },
  { source: 'status', header: 'Status', format: 'text' },
  { source: 'grossHours', header: 'Gross hours', format: 'hours' },
  { source: 'unpaidBreakHours', header: 'Unpaid break hours', format: 'hours' },
  { source: 'roundingAdjustmentHours', header: 'Rounding adjustment hours', format: 'hours' },
  { source: 'payableHours', header: 'Payable hours', format: 'hours' },
  { source: 'ordinaryHours', header: 'Ordinary hours', format: 'hours' },
  { source: 'overtimeHours', header: 'Overtime hours', format: 'hours' },
  { source: 'doubleTimeHours', header: 'Double time hours', format: 'hours' },
  { source: 'minimumShiftTopUpHours', header: 'Minimum shift top-up hours', format: 'hours' },
  { source: 'plannedHours', header: 'Planned hours', format: 'hours' },
  { source: 'varianceHours', header: 'Variance hours', format: 'hours' },
  { source: 'sundayHours', header: 'Sunday hours', format: 'hours' },
  { source: 'publicHolidayHours', header: 'Public holiday hours', format: 'hours' },
  { source: 'publicHolidayName', header: 'Public holiday', format: 'text' },
  { source: 'unsocialHours', header: 'Unsocial hours', format: 'hours' },
  { source: 'unsocialPremiumQualified', header: 'Unsocial premium qualified', format: 'boolean' },
  { source: 'hourlyRate', header: 'Hourly rate', format: 'money' },
  { source: 'ordinaryPay', header: 'Ordinary pay', format: 'money' },
  { source: 'overtimePay', header: 'Overtime pay', format: 'money' },
  { source: 'doubleTimePay', header: 'Double time pay', format: 'money' },
  { source: 'sundayPremiumPay', header: 'Sunday premium', format: 'money' },
  { source: 'publicHolidayPremiumPay', header: 'Public holiday premium', format: 'money' },
  { source: 'unsocialPremiumPay', header: 'Unsocial premium', format: 'money' },
  { source: 'estimatedBasePay', header: 'Estimated base pay', format: 'money' },
  { source: 'estimatedTotalPay', header: 'Estimated total pay', format: 'money' },
  { source: 'leaveAccruedHours', header: 'Leave accrued hours', format: 'hours' },
  { source: 'flags', header: 'Review flags', format: 'text' },
];

export const EMPLOYEE_SUMMARY_COLUMNS: ExportProfileColumn[] = [
  { source: 'employeeId', header: 'Employee ID', format: 'text' },
  { source: 'payrollReference', header: 'Payroll reference', format: 'text' },
  { source: 'employeeName', header: 'Employee Name', format: 'text' },
  { source: 'payBandCode', header: 'Pay band', format: 'text' },
  { source: 'employmentType', header: 'Employment type', format: 'text' },
  { source: 'costCentre', header: 'Cost centre', format: 'text' },
  { source: 'hourlyRate', header: 'Hourly rate', format: 'money' },
  { source: 'shiftCount', header: 'Shifts', format: 'number' },
  { source: 'completeShiftCount', header: 'Completed shifts', format: 'number' },
  { source: 'siteCount', header: 'Sites', format: 'number' },
  { source: 'payableHours', header: 'Payable hours', format: 'hours' },
  { source: 'ordinaryHours', header: 'Ordinary hours', format: 'hours' },
  { source: 'overtimeHours', header: 'Overtime hours', format: 'hours' },
  { source: 'doubleTimeHours', header: 'Double time hours', format: 'hours' },
  { source: 'unpaidBreakHours', header: 'Unpaid break hours', format: 'hours' },
  { source: 'sundayHours', header: 'Sunday hours', format: 'hours' },
  { source: 'publicHolidayHours', header: 'Public holiday hours', format: 'hours' },
  { source: 'unsocialHours', header: 'Unsocial hours', format: 'hours' },
  { source: 'unsocialShiftCount', header: 'Unsocial shifts', format: 'number' },
  { source: 'ordinaryPay', header: 'Ordinary pay', format: 'money' },
  { source: 'overtimePay', header: 'Overtime pay', format: 'money' },
  { source: 'doubleTimePay', header: 'Double time pay', format: 'money' },
  { source: 'sundayPremiumPay', header: 'Sunday premium', format: 'money' },
  { source: 'publicHolidayPremiumPay', header: 'Public holiday premium', format: 'money' },
  { source: 'unsocialPremiumPay', header: 'Unsocial premium', format: 'money' },
  { source: 'estimatedTotalPay', header: 'Estimated total pay', format: 'money' },
  { source: 'leaveAccruedHours', header: 'Leave accrued hours', format: 'hours' },
  { source: 'needsReviewCount', header: 'Lines needing review', format: 'number' },
];

export const COST_CENTRE_COLUMNS: ExportProfileColumn[] = [
  { source: 'groupLabel', header: 'Group', format: 'text' },
  { source: 'employeeCount', header: 'Employees', format: 'number' },
  { source: 'shiftCount', header: 'Shifts', format: 'number' },
  { source: 'payableHours', header: 'Payable hours', format: 'hours' },
  { source: 'ordinaryHours', header: 'Ordinary hours', format: 'hours' },
  { source: 'overtimeHours', header: 'Overtime hours', format: 'hours' },
  { source: 'doubleTimeHours', header: 'Double time hours', format: 'hours' },
  { source: 'sundayHours', header: 'Sunday hours', format: 'hours' },
  { source: 'publicHolidayHours', header: 'Public holiday hours', format: 'hours' },
  { source: 'unsocialHours', header: 'Unsocial hours', format: 'hours' },
  { source: 'ordinaryPay', header: 'Ordinary pay', format: 'money' },
  { source: 'overtimePay', header: 'Overtime pay', format: 'money' },
  { source: 'premiumPay', header: 'Premium pay', format: 'money' },
  { source: 'totalPay', header: 'Total pay', format: 'money' },
];

export const TIMESHEET_REGISTER_COLUMNS: ExportProfileColumn[] = [
  { source: 'periodLabel', header: 'Pay period', format: 'text' },
  { source: 'periodStart', header: 'Period start', format: 'date' },
  { source: 'periodEnd', header: 'Period end', format: 'date' },
  { source: 'periodStatus', header: 'Period status', format: 'text' },
  { source: 'payDate', header: 'Pay date', format: 'date' },
  { source: 'currencyCode', header: 'Currency code', format: 'text' },
  { source: 'payrollReference', header: 'Payroll reference', format: 'text' },
  { source: 'employeeId', header: 'Employee ID', format: 'text' },
  { source: 'employeeName', header: 'Employee', format: 'text' },
  { source: 'payBand', header: 'Pay band', format: 'text' },
  { source: 'baseHourlyRate', header: 'Base hourly rate', format: 'money' },
  { source: 'costCentre', header: 'Cost centre', format: 'text' },
  { source: 'approvalStatus', header: 'Approval status', format: 'text' },
  { source: 'approvedBy', header: 'Approved by', format: 'text' },
  { source: 'approvedAt', header: 'Approved at', format: 'datetime' },
  { source: 'calculatedAt', header: 'Calculated at', format: 'datetime' },
  { source: 'ordinaryHours', header: 'Non-leave ordinary hours', format: 'hours' },
  { source: 'overtimeHours', header: 'Overtime hours', format: 'hours' },
  { source: 'doubleTimeHours', header: 'Double-time hours', format: 'hours' },
  { source: 'unpaidBreakHours', header: 'Unpaid break hours', format: 'hours' },
  { source: 'sundayHours', header: 'Sunday hours', format: 'hours' },
  { source: 'publicHolidayHours', header: 'Public-holiday hours', format: 'hours' },
  { source: 'unsocialHours', header: 'Unsocial hours', format: 'hours' },
  { source: 'minimumTopUpHours', header: 'Minimum-shift top-up hours', format: 'hours' },
  { source: 'leaveHours', header: 'Leave hours', format: 'hours' },
  { source: 'leaveAccruedHours', header: 'Leave accrued hours', format: 'hours' },
  { source: 'shiftCount', header: 'Shifts', format: 'number' },
  { source: 'flaggedCount', header: 'Flagged lines', format: 'number' },
  { source: 'ordinaryPay', header: 'Ordinary pay (includes leave)', format: 'money' },
  { source: 'overtimePay', header: 'Overtime pay', format: 'money' },
  { source: 'premiumPay', header: 'Premium pay', format: 'money' },
  { source: 'adjustmentPay', header: 'Adjustments', format: 'money' },
  { source: 'expensePay', header: 'Expenses', format: 'money' },
  { source: 'deductionPay', header: 'Deductions', format: 'money' },
  { source: 'grossPay', header: 'Gross pay', format: 'money' },
  { source: 'amountPayable', header: 'Amount payable', format: 'money' },
];

export function getSystemColumns(kind: ExportProfileKind): ExportProfileColumn[] {
  switch (kind) {
    case 'employee_summary': return EMPLOYEE_SUMMARY_COLUMNS;
    case 'cost_centre': return COST_CENTRE_COLUMNS;
    case 'timesheet_register': return TIMESHEET_REGISTER_COLUMNS;
    default: return SHIFT_DETAIL_COLUMNS;
  }
}

function csvCell(value: string, delimiter: string): string {
  const needsQuoting = value.includes('"')
    || value.includes('\n')
    || value.includes('\r')
    || value.includes(delimiter);
  return needsQuoting ? `"${value.replace(/"/g, '""')}"` : value;
}

function formatDate(value: string, dateFormat: 'iso' | 'uk' | 'us'): string {
  if (dateFormat === 'iso' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const [year, month, day] = value.split('-');
  return dateFormat === 'uk' ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
}

/** Prevent user-controlled text cells from becoming spreadsheet formulas. */
function safeSpreadsheetText(value: string): string {
  return /^[\t\r\n ]*[=+\-@]/.test(value) ? `'${value}` : value;
}

function formatValue(
  raw: unknown,
  format: CellFormat,
  decimalPlaces: number,
  dateFormat: 'iso' | 'uk' | 'us',
): string {
  if (raw === null || raw === undefined) return '';

  if (Array.isArray(raw)) return safeSpreadsheetText(raw.join('; '));

  switch (format) {
    case 'money':
      return Number.isFinite(Number(raw)) ? Number(raw).toFixed(decimalPlaces) : '';
    case 'hours':
    case 'number':
      return Number.isFinite(Number(raw)) ? Number(raw).toFixed(format === 'number' ? 0 : 2) : '';
    case 'boolean':
      return raw ? 'Yes' : 'No';
    case 'date':
      return formatDate(String(raw), dateFormat);
    default:
      return safeSpreadsheetText(String(raw));
  }
}

interface CsvOptions {
  columns: ExportProfileColumn[];
  delimiter: string;
  includeHeader: boolean;
  includeBom: boolean;
  decimalPlaces: number;
  dateFormat: 'iso' | 'uk' | 'us';
}

function buildCsv(records: Record<string, unknown>[], options: CsvOptions): string {
  const lines: string[] = [];

  if (options.includeHeader) {
    lines.push(options.columns.map((column) => csvCell(
      safeSpreadsheetText(column.header),
      options.delimiter,
    )).join(options.delimiter));
  }

  records.forEach((record) => {
    lines.push(options.columns.map((column) => csvCell(
      formatValue(record[column.source], column.format ?? 'text', options.decimalPlaces, options.dateFormat),
      options.delimiter,
    )).join(options.delimiter));
  });

  const body = `${lines.join('\r\n')}\r\n`;
  return options.includeBom ? `﻿${body}` : body;
}

/** Rows for a given layout, drawn from an already-computed export result. */
export function getRecordsForKind(result: PayrollExportResult, kind: ExportProfileKind): Record<string, unknown>[] {
  switch (kind) {
    case 'employee_summary':
      return result.summaries as unknown as Record<string, unknown>[];
    case 'timesheet_register':
      throw new Error('Timesheet registers must be exported from a stored, locked pay period.');
    case 'cost_centre':
      return aggregateRows(result.rows, 'cost_centre') as unknown as Record<string, unknown>[];
    default:
      return result.rows as unknown as Record<string, unknown>[];
  }
}

export function buildCsvForProfile(result: PayrollExportResult, profile: PayrollExportProfile): string {
  const columns = profile.columns.length ? profile.columns : getSystemColumns(profile.kind);
  return buildCsv(getRecordsForKind(result, profile.kind), {
    columns,
    delimiter: profile.delimiter || ',',
    includeHeader: profile.includeHeader,
    includeBom: profile.includeBom,
    decimalPlaces: profile.decimalPlaces,
    dateFormat: profile.dateFormat,
  });
}

export function buildCsvForKind(result: PayrollExportResult, kind: ExportProfileKind): string {
  return buildCsv(getRecordsForKind(result, kind), {
    columns: getSystemColumns(kind),
    delimiter: ',',
    includeHeader: true,
    includeBom: true,
    decimalPlaces: 2,
    dateFormat: 'iso',
  });
}

/** Back-compatible default export: the shift detail layout. */
export function buildPayrollCsv(result: PayrollExportResult): string {
  return buildCsvForKind(result, 'shift_detail');
}

export function buildPayrollFilename(result: PayrollExportResult, kind: ExportProfileKind = 'shift_detail'): string {
  const period = result.startDate && result.endDate
    ? `${result.startDate}_to_${result.endDate}`
    : 'all_time';
  const company = result.companyName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'company';
  const suffix = kind === 'shift_detail' ? 'payroll' : `payroll_${kind}`;

  return `${company}_${suffix}_${period}.csv`;
}

/**
 * Export the stored timesheet snapshots for one pay period.
 *
 * Unlike the legacy `timesheet_register` layout on PayrollExportResult, this
 * register never recalculates live shifts: it serialises the figures and
 * approval metadata held on Timesheet rows, which is the stable source for a
 * locked payroll period.
 */
export function buildTimesheetRegisterCsv(
  period: PayPeriod,
  timesheets: Timesheet[],
  fallbackCurrencyCode = 'EUR',
): string {
  const snapshotCurrency = period.calculationCurrencyCode?.trim().toUpperCase() ?? '';
  const fallbackCurrency = fallbackCurrencyCode.trim().toUpperCase();
  const currencyCode = /^[A-Z]{3}$/.test(snapshotCurrency)
    ? snapshotCurrency
    : /^[A-Z]{3}$/.test(fallbackCurrency) ? fallbackCurrency : 'EUR';

  const records = timesheets.map((timesheet) => ({
    periodLabel: period.label,
    periodStart: period.startDate,
    periodEnd: period.endDate,
    periodStatus: period.status,
    payDate: period.payDate,
    currencyCode,
    payrollReference: timesheet.payrollReference,
    employeeId: timesheet.guardId,
    employeeName: timesheet.guardName,
    payBand: timesheet.payBandCode,
    baseHourlyRate: timesheet.baseHourlyRate,
    costCentre: timesheet.costCentre,
    approvalStatus: timesheet.status,
    approvedBy: timesheet.approvedByName,
    approvedAt: timesheet.approvedAt,
    calculatedAt: timesheet.calculatedAt,
    // The cached ordinary total includes paid-leave minutes. Keep the two
    // register columns mutually exclusive so a bureau that maps both hours
    // does not pay the leave twice. Money remains one combined stored amount
    // until the snapshot schema has a dedicated leave-pay component.
    ordinaryHours: Math.max(timesheet.ordinaryMinutes - timesheet.leaveMinutes, 0) / 60,
    overtimeHours: timesheet.overtimeMinutes / 60,
    doubleTimeHours: timesheet.doubleTimeMinutes / 60,
    unpaidBreakHours: timesheet.unpaidBreakMinutes / 60,
    sundayHours: timesheet.sundayMinutes / 60,
    publicHolidayHours: timesheet.publicHolidayMinutes / 60,
    unsocialHours: timesheet.unsocialMinutes / 60,
    minimumTopUpHours: timesheet.minimumTopUpMinutes / 60,
    leaveHours: timesheet.leaveMinutes / 60,
    leaveAccruedHours: timesheet.leaveAccruedHours,
    shiftCount: timesheet.shiftCount,
    flaggedCount: timesheet.flaggedCount,
    ordinaryPay: timesheet.ordinaryPay,
    overtimePay: timesheet.overtimePay,
    premiumPay: timesheet.premiumPay,
    adjustmentPay: timesheet.adjustmentPay,
    expensePay: timesheet.expensePay,
    deductionPay: timesheet.deductionPay,
    grossPay: timesheet.grossPay,
    amountPayable: timesheet.grossPay + timesheet.expensePay + timesheet.deductionPay,
  }));

  return buildCsv(records, {
    columns: TIMESHEET_REGISTER_COLUMNS,
    delimiter: ',',
    includeHeader: true,
    includeBom: true,
    decimalPlaces: 2,
    dateFormat: 'iso',
  });
}

function filenameToken(value: string, fallback: string): string {
  const token = value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

  return token || fallback;
}

/** A path-safe, deterministic filename for a stored timesheet register. */
export function buildTimesheetRegisterFilename(period: PayPeriod): string {
  const label = filenameToken(period.label, 'pay-period');
  const start = filenameToken(period.startDate, 'start');
  const end = filenameToken(period.endDate, 'end');
  return `timesheet_register_${label}_${start}_to_${end}.csv`;
}
